export const LR_COPY_TYPES = ['Consignor Copy', 'Consignee Copy', 'POD Copy'] as const;
export type LrCopyType = typeof LR_COPY_TYPES[number];

// Traditional carbonless-copy color coding: Consignor = light pink,
// Consignee = light blue, POD (original) = plain white.
const LR_COPY_COLORS: Record<string, string> = {
  'Consignor Copy': '#fde7ef',
  'Consignee Copy': '#e5f2ff',
  'POD Copy': '#ffffff',
};

function getCopyColor(copyType: string): string {
  return LR_COPY_COLORS[copyType] || '#ffffff';
}

// Path to the company logo image. Point this at wherever the logo asset
// actually lives in the Angular project (e.g. assets folder or a CDN URL).
export const DEFAULT_LOGO_URL = 'https://apollosupplychain.com/wp-content/uploads/2025/12/Apollo-Supply-Chain-Logo.png';

const LR_STYLES = `
  * { box-sizing: border-box; }
  body {
    font-family: 'Segoe UI', Arial, sans-serif;
    background: #fff;
    margin: 0;
    padding: 16px;
  }
  .lr-sheet {
    max-width: 850px;
    margin: 0 auto 24px;
    background: #fff;
    border: 2px solid #000;
    font-size: 13px;
    color: #111;
  }
  .lr-header {
    display: flex;
    align-items: stretch;
    border-bottom: 2px solid #000;
  }
  .lr-logo {
    width: 90px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 2px solid #000;
    padding: 6px;
  }
  .lr-logo-img {
    max-width: 100%;
    max-height: 60px;
    object-fit: contain;
  }
  .lr-title-block { flex: 1; text-align: center; padding: 6px 4px; }
  .lr-jurisdiction { font-size: 11px; font-style: italic; }
  .lr-company-name { font-size: 22px; font-weight: 800; letter-spacing: 0.5px; margin: 2px 0; }
  .lr-copy-type {
    width: 130px; display: flex; align-items: center; justify-content: center;
    border-left: 2px solid #000; font-size: 12px; font-weight: 600; text-align: center; padding: 4px;
  }
  .lr-office-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 4px 10px; border-bottom: 2px solid #000; font-size: 11.5px;
  }
  .lr-info-grid { display: flex; border-bottom: 2px solid #000; }
  .lr-info-left { flex: 2.5; border-right: 2px solid #000; }
  .lr-info-right { flex: 1; }
  .lr-field-row {
    display: flex; border-bottom: 1px solid #ccc; padding: 5px 8px; align-items: baseline; gap: 6px;
  }
  .lr-field-row:last-child { border-bottom: none; }
  .lr-label { font-weight: 600; white-space: nowrap; font-size: 12px; }
  .lr-value { flex: 1; border-bottom: 1px dotted #444; min-height: 16px; font-weight: 500; }
  .lr-two-col { display: flex; gap: 14px; flex: 1; }
  .lr-two-col .lr-field-row { flex: 1; }

  table.lr-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  table.lr-table th, table.lr-table td {
    border: 1.5px solid #000; padding: 4px 4px; vertical-align: top;
  }
  table.lr-table th { background: rgba(0,0,0,0.04); font-weight: 700; text-align: center; }  .lr-cell-blank { height: 150px; }
  .weight-sub-th { text-align: center; font-weight: 700; }

  .lr-table-footer-row td { height: 74px; vertical-align: bottom; padding-bottom: 10px; }
  .lr-empty-td { border-right: none; border-left: none; }
  .lr-freight-cell { font-weight: 600; text-align: left; white-space: nowrap; }
  .lr-signatory-cell { font-weight: 600; text-align: center; white-space: nowrap; }

  .lr-bottom-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 8px 10px 10px; font-size: 11.5px;
  }
  .lr-gst-note { font-weight: 600; }
  .lr-terms-ref { font-size: 10.5px; font-style: italic; }

  @media print {
    body { padding: 0; }
    .lr-sheet { border: 2px solid #000; margin-bottom: 0; page-break-after: always; }
    .lr-sheet:last-of-type { page-break-after: auto; }
  }
`;

// One printable sheet — a single copy (Consignor/Consignee/POD) of a
// single LR. Multiple of these get concatenated to build the full
// document when there are multiple LRs and/or multiple copy types.
const LR_SHEET_TEMPLATE = `
  <div class="lr-sheet" style="background:{{sheetBgColor}};">

    <div class="lr-header">
      <div class="lr-logo">
        <img class="lr-logo-img" src="{{logoUrl}}" alt="Company Logo" />
      </div>
      <div class="lr-title-block">
        <div class="lr-jurisdiction">Subject to Gurgaon Jurisdiction</div>
        <div class="lr-company-name">APOLLO Supply Chain Pvt. Ltd.</div>
      </div>
      <div class="lr-copy-type">{{copyType}}</div>
    </div>

    <div class="lr-office-row">
      <span>Corporate Office: Apollo Towers, Plot No. 20, Sector-44, Gurugram, Haryana-122002</span>
      <span>GSTIN : 06AALCA0748D1ZY</span>
    </div>

    <div class="lr-info-grid">
      <div class="lr-info-left">
        <div class="lr-field-row">
          <span class="lr-label">Consignor</span>
          <span class="lr-value">{{consignorName}}</span>
        </div>
        <div class="lr-two-col">
          <div class="lr-field-row">
            <span class="lr-label">Pincode No.</span>
            <span class="lr-value">{{consignorPincode}}</span>
          </div>
        </div>
        <div class="lr-two-col">
          <div class="lr-field-row">
            <span class="lr-label">GST No.</span>
            <span class="lr-value">{{consignorGst}}</span>
          </div>
          <div class="lr-field-row">
            <span class="lr-label">Phone No.</span>
            <span class="lr-value">{{consignorPhone}}</span>
          </div>
        </div>
        <div class="lr-field-row">
          <span class="lr-label">Consignee</span>
          <span class="lr-value">{{consigneeName}}</span>
        </div>
        <div class="lr-two-col">
          <div class="lr-field-row">
            <span class="lr-label">Pincode No.</span>
            <span class="lr-value">{{consigneePincode}}</span>
          </div>
        </div>
        <div class="lr-two-col">
          <div class="lr-field-row">
            <span class="lr-label">GST No.</span>
            <span class="lr-value">{{consigneeGst}}</span>
          </div>
          <div class="lr-field-row">
            <span class="lr-label">Phone No.</span>
            <span class="lr-value">{{consigneePhone}}</span>
          </div>
        </div>
      </div>

      <div class="lr-info-right">
        <div class="lr-field-row">
          <span class="lr-label">G R No.<br/>(Non-negotiable)</span>
          <span class="lr-value">{{grNo}}</span>
        </div>
        <div class="lr-field-row">
          <span class="lr-label">Date</span>
          <span class="lr-value">{{lrDate}}</span>
        </div>
        <div class="lr-field-row">
          <span class="lr-label">From</span>
          <span class="lr-value">{{from}}</span>
        </div>
        <div class="lr-field-row">
          <span class="lr-label">To</span>
          <span class="lr-value">{{to}}</span>
        </div>
        <div class="lr-field-row">
          <span class="lr-label">Lorry No.</span>
          <span class="lr-value">{{lorryNo}}</span>
        </div>
        <div class="lr-field-row">
          <span class="lr-label">Vehicle Type</span>
          <span class="lr-value">{{vehicleType}}</span>
        </div>
      </div>
    </div>

    <table class="lr-table">
      <thead>
        <tr>
          <th style="width: 26%;">Invoice/STN No.<br/>&amp; E-Way Bill No.</th>
          <th style="width: 19%;">Container No./Loose Cargo<br/>Said to Contain</th>
          <th style="width: 12%;">Value of<br/>Goods</th>
          <th colspan="2" style="width: 16%;">Weight</th>
          <th style="width: 12%;">No of<br/>Pkg(s)</th>
          <th style="width: 18%;">Seal No./OTL No.</th>
        </tr>
        <tr>
          <th></th><th></th><th></th>
          <th class="weight-sub-th">Actual</th>
          <th class="weight-sub-th">Charged</th>
          <th></th><th></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="lr-cell-blank">{{invoiceEwayValidLabel}}</td>
          <td class="lr-cell-blank">{{containerCargo}}</td>
          <td class="lr-cell-blank">{{valueOfGoods}}</td>
          <td class="lr-cell-blank">{{weightActual}}</td>
          <td class="lr-cell-blank">{{weightCharged}}</td>
          <td class="lr-cell-blank">{{noOfPkgs}}</td>
          <td class="lr-cell-blank">{{sealNo}}</td>
        </tr>
        <tr class="lr-table-footer-row">
          <td colspan="5" class="lr-empty-td"></td>
          <td class="lr-freight-cell">Freight To Be Billed</td>
          <td class="lr-signatory-cell">Authorised Signatory</td>
        </tr>
      </tbody>
    </table>

    <div class="lr-bottom-row">
      <span class="lr-gst-note">GST TO BE PAID BY CONSIGNOR OR CONSIGNEE</span>
      <span class="lr-terms-ref">Terms and Condition overleaf</span>
    </div>

  </div>
`;

export interface LrTemplateData {
  copyType: string;
  logoUrl: string;
  sheetBgColor: string;

  consignorName: string;
  consignorPincode: string;
  consignorGst: string;
  consignorPhone: string;
  consigneeName: string;
  consigneePincode: string;
  consigneeGst: string;
  consigneePhone: string;
  grNo: string;
  lrDate: string;
  from: string;
  to: string;
  lorryNo: string;
  vehicleType: string;
  invoiceEwayValidLabel: string;
  containerCargo: string;
  valueOfGoods: string;
  weightActual: string;
  weightCharged: string;
  noOfPkgs: string;
  sealNo: string;
}

/**
 * Maps live trip data -> printed LR fields for ONE copy of ONE LR.
 * Call once per (LR row x copy type) combination — see buildLrCopiesForRow
 * and buildLrDocumentData below for the multi-LR / multi-copy driver.
 */
export function buildLrTemplateData(
  order: any,
  lrRow: any,
  lhcRow: any,
  loadingForm: any,
  ewayBill: any,
  copyType: string,
  vehicleTypeLabel?: string,
  logoUrl: string = DEFAULT_LOGO_URL,
  pickupRow?: any,
  dropRow?: any
): LrTemplateData {
  return {
    copyType,
    logoUrl,
    sheetBgColor: getCopyColor(copyType),

    consignorName: order?.customerName || '--',
    // Pincode is not on the order itself — it comes from this LR's
    // linked pickup location (Section 3d, matched via pickup.lrId).
    consignorPincode: pickupRow?.pincode || order?.customerPincode || '--',
    consignorGst: order?.customerGst || '--',
    consignorPhone: order?.customerPhone || '--',

    consigneeName: order?.receiverName || '--',
    // Same idea, from this LR's linked drop location.
    consigneePincode: dropRow?.pincode || order?.receiverPincode || '--',
    consigneeGst: order?.receiverGst || '--',
    consigneePhone: order?.receiverPhone || '--',

    grNo: lrRow?.lrNumber || '--',
    lrDate: formatDate(lrRow?.lrDate),
    from: order?.source || '--',
    to: order?.destination || '--',
    lorryNo: lhcRow?.vehicleNumber || '--',
    vehicleType: vehicleTypeLabel || order?.vehicleTypeLabel || '--',

    invoiceEwayValidLabel: buildInvoiceEwayValidLabel(ewayBill),
    containerCargo: loadingForm?.materialTypeDescription || '--',
    valueOfGoods: ewayBill?.invoiceValue ? formatCurrency(ewayBill.invoiceValue) : '--',
    weightActual: loadingForm?.weight ? `${loadingForm.weight} T` : '--',
    weightCharged: loadingForm?.chargedWeight ? `${loadingForm.chargedWeight} T` : '--',
    noOfPkgs: loadingForm?.numberOfPackages ?? '--',
    sealNo: loadingForm?.sealNumber || '--',
  };
}

/**
 * Builds all 3 copies (Consignor / Consignee / POD) for a single LR row.
 */
export function buildLrCopiesForRow(
  order: any,
  lrRow: any,
  lhcRow: any,
  loadingForm: any,
  ewayBill: any,
  vehicleTypeLabel?: string,
  logoUrl?: string,
  pickupRow?: any,
  dropRow?: any
): LrTemplateData[] {
  return LR_COPY_TYPES.map((copyType) =>
    buildLrTemplateData(order, lrRow, lhcRow, loadingForm, ewayBill, copyType, vehicleTypeLabel, logoUrl, pickupRow, dropRow)
  );
}

/**
 * Builds the full flat list of sheets to print/download: every LR row
 * gets Consignor + Consignee + POD copies, in that order, one after
 * another. `matchEwayBillForLr` lets the caller pick which fetched
 * e-Way bill (if any) belongs to a given LR row.
 */
export function buildLrDocumentData(
  order: any,
  lrRows: any[],
  lhcRow: any,
  loadingForm: any,
  ewayRows: any[],
  vehicleTypeLabel?: string,
  logoUrl?: string,
  matchEwayBillForLr?: (lrRow: any) => any,
  matchLocationsForLr?: (lrRow: any) => { pickupRow?: any; dropRow?: any }
): LrTemplateData[] {
  const result: LrTemplateData[] = [];
  (lrRows || []).forEach((lrRow) => {
    if (!lrRow?.lrNumber) return; // skip empty/unfilled LR rows
    const ewayBill = matchEwayBillForLr
      ? matchEwayBillForLr(lrRow)
      : (ewayRows || []).find((b) => b.fetched);
    const { pickupRow, dropRow } = matchLocationsForLr
      ? matchLocationsForLr(lrRow)
      : ({} as { pickupRow?: any; dropRow?: any });
    result.push(
      ...buildLrCopiesForRow(order, lrRow, lhcRow, loadingForm, ewayBill, vehicleTypeLabel, logoUrl, pickupRow, dropRow)
    );
  });
  return result;
}

function buildInvoiceEwayValidLabel(ewayBill: any): string {
  const invoiceNo = ewayBill?.invoiceNo || '--';
  const ewbNo = ewayBill?.ewayBillNumber || '--';
  const validUpto = ewayBill?.validUptoObj ? formatDate(ewayBill.validUptoObj) : '--';
  return `Inv: ${invoiceNo}<br/>EWB: ${ewbNo}<br/>Valid: ${validUpto}`;
}

function fillSheet(data: LrTemplateData): string {
  let html = LR_SHEET_TEMPLATE;
  Object.keys(data).forEach((key) => {
    const token = new RegExp(`{{${key}}}`, 'g');
    html = html.replace(token, String((data as any)[key] ?? '--'));
  });
  return html;
}

/**
 * Renders a single sheet as a *standalone* HTML document. Kept for
 * backwards compatibility — prefer renderLrDocument for the real
 * multi-LR / multi-copy flow.
 */
export function renderLrHtml(data: LrTemplateData): string {
  return renderLrDocument([data]);
}

/**
 * Renders every sheet in `dataList` into one HTML document, each sheet
 * on its own printed page (page-break-after in print CSS).
 */
export function renderLrDocument(dataList: LrTemplateData[]): string {
  const grNo = dataList[0]?.grNo || '';
  const sheetsHtml = dataList.map(fillSheet).join('\n');
  return `
<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<title>Lorry Receipt - ${grNo}</title>
<style>${LR_STYLES}</style>
</head>
<body>
${sheetsHtml}
</body>
</html>
`;
}

export function printLrHtml(filledHtml: string): void {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  printWindow.document.open();
  printWindow.document.write(filledHtml);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
}

export function downloadLrHtml(filledHtml: string, fileName: string): void {
  const blob = new Blob([filledHtml], { type: 'text/html' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(url);
}

function formatDate(value: any): string {
  if (!value) return '--';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatCurrency(value: number): string {
  if (value == null) return '--';
  return `₹${Number(value).toLocaleString('en-IN')}`;
}